import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import { Row, Col, Button, Glyphicon, FormGroup, FormControl, ControlLabel } from 'react-bootstrap';
import moment from 'moment';

import helpers from '../../lib/helpers';
import ReusableTable from '../tables/ReusableTable.jsx';
import ActionCellRenderer from '../tables/ActionCellRenderer.jsx';
import validation from '../../lib/validation';

import styles from '../../styles/variables.scss';

import { updateField, uploadDocument } from '../../actions/form';

export class DocumentUploaderForm extends Component {
  constructor(props) {
    super(props);

    /* istanbul ignore next */
    this.defaultState = {
      inputs: {
        name: '',
        type: '',
        upload: '',
        blob: '',
      },
      btnContent: 'No document selected',
      rules: {},
      addEnabled: true,
    };

    this.state = _.cloneDeep(this.defaultState);

    this.columnDefs = [
      { headerName: 'Name', field: 'name', minWidth: 200 },
      { headerName: 'Upload Date', field: 'date', minWidth: 100 },
      { headerName: 'Document Type', field: 'type', minWidth: 100 },
      { headerName: 'Size', field: 'size', minWidth: 100 },
      {
        headerName: 'Actions',
        field: 'actions',
        minWidth: 100,
        cellRendererFramework: ActionCellRenderer,
      },
    ];

    this.onNameChange = this.onNameChange.bind(this);
    this.onTypeChange = this.onTypeChange.bind(this);
    this.onUploadChange = this.onUploadChange.bind(this);
    this.onAdd = this.onAdd.bind(this);
    this.resetFields = this.resetFields.bind(this);
    this.validateAll = this.validateAll.bind(this);
    this.getValidationStates = this.getValidationStates.bind(this);
    this.onDocUploadClick = this.onDocUploadClick.bind(this);
  }

  componentDidUpdate() {
    if (this.props.rules && !_.isEqual(this.props.rules, this.state.rules)) {
      this.setState({ rules: this.props.rules });
    }
  }

  onDocUploadClick() {
    ReactDOM.findDOMNode(this.fileUploadInput).click();
  }

  onNameChange(e) {
    const updates = this.state;

    if (e.target.value === '' || /^[^[/?*:;{}<>'"\\\]]+$/.test(e.target.value)) {
      updates.inputs.name = e.target.value;
    }

    this.setState(updates);
  }

  onTypeChange(e) {
    const updates = this.state;
    updates.inputs.type = e.target.value;
    this.setState(updates);
  }

  onUploadChange(e) {
    e.preventDefault();
    const upload = e.target.value;
    const updates = this.state;
    updates.inputs.upload = upload;
    this.setState(updates);
    let files;

    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }

    if (files.length && files[0].name) {
      const fileText = files[0].name;
      updates.btnContent = `Current file selected: ${fileText}`;
    }

    return helpers.readFileAsDataUrl(files[0]).then((blob) => {
      updates.inputs.blob = blob;
      this.setState(updates);
    }).catch(() => {
      updates.btnContent = 'Select Content';
    });
  }

  onAdd() {
    if (!this.validateAll()) return;

    const [mime, blob] = this.state.inputs.blob.split('data:')[1].split(';base64,');
    const doc = {
      mime,
      data: helpers.base64toBlob(blob, mime),
      name: this.state.inputs.name,
      type: this.state.inputs.type,
      date: moment().format('M-D-YYYY'),
    };

    this.props.uploadDocument(doc);
    this.setState(this.defaultState);
  }

  getValidationStates() {
    const fieldValues = this.state.inputs;
    let name = null;
    let type = null;
    let upload = null;

    if (fieldValues.name !== '') {
      name = this.validateName() ? 'success' : 'error';
    }

    if (fieldValues.type !== '') {
      type = this.validateType() ? 'success' : 'error';
    }

    if (fieldValues.upload !== '') {
      upload = this.validateUpload() ? 'success' : 'error';
    }

    return { name, type, upload };
  }

  resetFields() {
    const updates = _.cloneDeep(this.state);
    updates.btnContent = 'No document selected';
    updates.inputs = {
      name: '',
      type: '',
      upload: '',
      blob: '',
    };
    updates.addEnabled = true;
    this.setState(updates);
  }

  validateName() {
    const name = this.state.inputs.name;
    return validation.run(_.get(this.state, 'rules.name'), name);
  }

  validateType() {
    const type = this.state.inputs.type;
    return validation.run(_.get(this.state, 'rules.type'), type);
  }

  validateUpload() {
    const ext = this.state.inputs.upload.split('.');
    const types = ['pdf', 'xls', 'xlsx', 'doc', 'docx', 'rtf', 'csv', 'txt'];

    return (ext.length > 1 && types.indexOf(ext[ext.length - 1]) !== -1);
  }

  validateAll() {
    const validations = [
      this.validateName(),
      this.validateType(),
      this.validateUpload(),
    ];
    let valid = true;

    validations.forEach((_validation) => {
      valid = valid && _validation;
    });

    return valid;
  }

  render() {
    const validationStates = this.getValidationStates();

    return (
      <div className="container-fluid form-container">
        <Row>
          <Col lg={12} className="form-title-container">
            <h1>{this.props.currentUserLevel}</h1>
            <span className="form-title-span">{this.props.name}</span>
            <p className="subtext">(May add Race Schedules, Race History, Statistics, Marketing Decks etc.)</p>
          </Col>
        </Row>
        <form id="document-uploader-form">
          <Row>
            <Col xs={12} lg={6}>
              <FormGroup validationState={validationStates.name} controlId="nameInput">
                <FormControl
                  type="text"
                  name="name"
                  value={this.state.inputs.name}
                  placeholder="Name"
                  onChange={this.onNameChange}
                />
                <FormControl.Feedback />
              </FormGroup>
            </Col>
            <Col xs={6} lg={3}>
              <FormGroup validationState={validationStates.type} controlId="documentTypeInput" className="dropdownSelect">
                <FormControl
                  value={this.state.inputs.type}
                  className="stateComboBoxControl"
                  componentClass="select"
                  onChange={this.onTypeChange}
                  placeholder="Document Type"
                >
                  <option value="">Document Type</option>
                  <option value="Agreement">Agreement</option>
                  <option value="Contract">Contract</option>
                  <option value="Resume">Resume</option>
                  <option value="Schedule">Schedule</option>
                  <option value="Misc">Misc.</option>
                </FormControl>
                <FormControl.Feedback id="documentTypeInputFeedback"/>
                <ControlLabel className="glyphicon glyphicon-chevron-down dropdown-arrow" />
              </FormGroup>
            </Col>
            <Col xs={6} lg={3}>
              <FormGroup validationState={validationStates.upload} id="fileUploadInputGroup">
                <Button
                  label="File Upload"
                  id="fileUploadBtn"
                  onClick={this.onDocUploadClick}
                >
                  <Glyphicon glyph="glyphicon glyphicon-option-horizontal pull-left" /> Select a Document</Button>
                <FormControl
                  type="file"
                  id="fileUploadInput"
                  ref={(c) => { this.fileUploadInput = c; }}
                  value={this.state.inputs.upload}
                  onChange={this.onUploadChange}
                />
                <FormControl.Feedback />
              </FormGroup>
            </Col>
            <Col sm={12} className="document-text-container">
              <p className="pull-right document-text">{this.state.btnContent}</p>
            </Col>
          </Row>
          <Row>
            <div className="form-control-btn-container pull-right">
              <Button onClick={this.resetFields} style={{ marginRight: '5px' }}>
                <Glyphicon glyph="remove" /> Cancel
              </Button>
              <Button
                onClick={this.onAdd}
                disabled={!this.state.addEnabled}
                id="addDocBtn"
              >
                <Glyphicon glyph="plus" /> Add Document
              </Button>
            </div>
          </Row>
          <Row>
            <ReusableTable columnDefs={this.columnDefs} documents={this.props.documents} />
          </Row>
        </form>

        <style jsx global>{`
          #document-uploader-form {
            input[type="file"] {
              display: none;
            }

            .document-text {
              color: ${styles.offwhite};
              font-size: .9em;
            }

            #fileUploadBtn {
              margin-top: 0px;
              width: 100%;
              display: flex;
              justify-content: center;

              .glyphicon {
                margin: 3px 10px 0 0;
              }
            }
          }
        `}</style>
      </div>
    );
  }
}

export const mapStateToProps = state => ({
  documents: _.get(state, 'form.profile.fields.documents', []),
});

export default connect(
  mapStateToProps,
  { updateField, uploadDocument },
)(DocumentUploaderForm);
